import { z } from 'zod'

/**
 * An API endpoint's Monthly Uptime Percentage for a UTC month (CLI-1041): the contract's figure, which
 * clipbook-api measures from real requests by the same rule as its monthly SLA report. Not the five-minute
 * health samples of api-health.ts, which are Clipbook's internal signal.
 */

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/
/** Cut to two decimals by clipbook-api, never rounded up, so 99.89% cannot read as 99.90%. */
const PERCENT = /^\d{1,3}\.\d{2}$/

/** clipbook-api's answer to `GET /internal/uptime`, parsed at the boundary. */
export const clipbookApiUptimeResponseSchema = z.object({
  endpoint_id: z.uuid(),
  month: z.string().regex(MONTH),
  through: z.iso.datetime().nullable(),
  minutes_with_requests: z.number().int().nonnegative(),
  downtime_minutes: z.number().int().nonnegative(),
  uptime_percent: z.string().regex(PERCENT),
})
export type ClipbookApiUptimeResponse = z.infer<typeof clipbookApiUptimeResponseSchema>

/** The workspace API page's read of it, for the current UTC month. */
export const apiUptimeSchema = z.object({
  endpointId: z.uuid(),
  month: z.string().regex(MONTH),
  /** Where a running month's figures end; its Downtime can only grow. Null once the month is over. */
  through: z.iso.datetime().nullable(),
  /** Zero means nothing was measured: the arithmetic's 100% says nothing, so the page shows no figure. */
  minutesWithRequests: z.number().int().nonnegative(),
  downtimeMinutes: z.number().int().nonnegative(),
  uptimePercent: z.string().regex(PERCENT),
})
export type ApiUptime = z.infer<typeof apiUptimeSchema>
